import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useStorage } from '../../hooks/useStorage';
import { pueblosPuertoRico } from '../../mocks/pueblos';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

type TipoPerfil = 'comprador' | 'vendedor';
type TipoIdentificacion = 'licencia' | 'pasaporte' | 'id_estatal' | 'real_id';

export default function RegistroPage() {
  const navigate = useNavigate();
  const { register, user, profile } = useAuth();
  const { uploadIdentificacion, uploading, uploadProgress } = useStorage();
  const [tipoPerfil, setTipoPerfil] = useState<TipoPerfil | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailConfirmacion, setEmailConfirmacion] = useState(false);
  const [aceptaTerminos, setAceptaTerminos] = useState(false);

  // Campos comunes
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [telefonoComprador, setTelefonoComprador] = useState('');

  // Campos de vendedor
  const [nombreCompleto, setNombreCompleto] = useState('');
  const [pueblo, setPueblo] = useState('');
  const [telefono, setTelefono] = useState('');
  const [direccion, setDireccion] = useState('');
  const [tipoIdentificacion, setTipoIdentificacion] = useState<TipoIdentificacion>('licencia');
  const [numeroIdentificacion, setNumeroIdentificacion] = useState('');
  const [archivoIdentificacion, setArchivoIdentificacion] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  // Redirigir después del registro exitoso
  useEffect(() => {
    if (user && profile) {
      if (profile.tipo_perfil === 'vendedor') {
        navigate('/dashboard');
      } else {
        navigate('/');
      }
    }
  }, [user, profile, navigate]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setError('Solo se permiten archivos JPG, PNG o PDF');
      return;
    }

    // Validar tamaño (máximo 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('El archivo no puede superar los 5MB');
      return;
    }

    setArchivoIdentificacion(file);
    setError('');

    // Crear preview solo para imágenes
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validaciones
    if (!aceptaTerminos) {
      setError('Debes aceptar los Términos y Condiciones para continuar');
      return;
    }

    if (!email || !password || !confirmPassword) {
      setError('Por favor completa todos los campos obligatorios');
      return;
    }

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (tipoPerfil === 'vendedor') {
      if (!nombreCompleto || !pueblo || !telefono || !numeroIdentificacion || !direccion) {
        setError('Por favor completa todos los campos obligatorios');
        return;
      }

      if (!archivoIdentificacion) {
        setError('Debes subir una imagen de tu identificación');
        return;
      }

      if (telefono.length < 10) {
        setError('El teléfono debe tener al menos 10 dígitos');
        return;
      }
    }

    setLoading(true);
    try {
      let urlIdentificacion = '';

      if (tipoPerfil === 'vendedor' && archivoIdentificacion) {
        const tempUserId = `temp_${Date.now()}`;
        const uploadResult = await uploadIdentificacion(archivoIdentificacion, tempUserId);

        if (!uploadResult.success) {
          setError(uploadResult.error || 'Error al subir la identificación');
          setLoading(false);
          return;
        }

        urlIdentificacion = uploadResult.url || '';
      }

      const profileData = tipoPerfil === 'vendedor' ? {
        nombre_completo: nombreCompleto,
        pueblo,
        telefono,
        direccion,
        tipo_identificacion: tipoIdentificacion,
        numero_identificacion: numeroIdentificacion,
        url_identificacion: urlIdentificacion,
      } : {
        telefono: telefonoComprador || null,
      };

      const result = await register(email, password, tipoPerfil!, profileData);

      if (!result.success) {
        // Traducir mensajes de Supabase al español
        const raw = result.error || '';
        if (
          raw.toLowerCase().includes('already registered') ||
          raw.toLowerCase().includes('user already exists') ||
          raw.toLowerCase().includes('email address is already')
        ) {
          setError('Este correo electrónico ya está registrado. ¿Quieres iniciar sesión?');
        } else if (raw.toLowerCase().includes('invalid email')) {
          setError('Por favor ingresa un correo electrónico válido');
        } else if (raw.toLowerCase().includes('password')) {
          setError('La contraseña debe tener al menos 6 caracteres');
        } else if (raw.toLowerCase().includes('database') || raw.toLowerCase().includes('violates')) {
          setError('Error al guardar tu perfil. Intenta de nuevo o contacta soporte');
        } else {
          setError(raw || 'Error al crear la cuenta. Por favor intenta de nuevo');
        }
        setLoading(false);
        return;
      }

      // Verificar si necesita confirmación de email
      if (result.needsEmailConfirmation) {
        setEmailConfirmacion(true);
        setLoading(false);
        return;
      }

      // La redirección se maneja en el useEffect cuando user y profile se cargan
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al crear la cuenta';
      setError(message);
      setLoading(false);
    }
  };

  if (!tipoPerfil) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Navbar />
        
        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <div className="w-full max-w-4xl">
            <div className="text-center mb-12">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Crear Cuenta en IslaVende
              </h1>
              <p className="text-gray-600">
                Selecciona el tipo de cuenta que deseas crear
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <button
                onClick={() => setTipoPerfil('comprador')}
                className="bg-white border-2 border-gray-200 hover:border-cobalt-500 rounded-lg p-8 text-left transition-all group cursor-pointer"
              >
                <div className="w-16 h-16 bg-cobalt-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-cobalt-500 transition-colors">
                  <i className="ri-shopping-cart-line text-3xl text-cobalt-500 group-hover:text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Comprador
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Encuentra productos increíbles en toda la isla con envío garantizado
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center">
                    <i className="ri-check-line text-green-600 mr-2"></i>
                    Compra segura con protección
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-green-600 mr-2"></i>
                    Envío a toda la isla
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-green-600 mr-2"></i>
                    Registro rápido
                  </li>
                </ul>
              </button>

              <button
                onClick={() => setTipoPerfil('vendedor')}
                className="bg-white border-2 border-gray-200 hover:border-cobalt-500 rounded-lg p-8 text-left transition-all group cursor-pointer"
              >
                <div className="w-16 h-16 bg-cobalt-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-cobalt-500 transition-colors">
                  <i className="ri-store-line text-3xl text-cobalt-500 group-hover:text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Vendedor
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Vende tus productos a miles de compradores en Puerto Rico
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center">
                    <i className="ri-check-line text-green-600 mr-2"></i>
                    Tu propia tienda online
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-green-600 mr-2"></i>
                    Gestión de inventario
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-green-600 mr-2"></i>
                    Pagos seguros
                  </li>
                </ul>
              </button>
            </div>

            <div className="mt-8 text-center">
              <p className="text-gray-600 text-sm">
                ¿Ya tienes cuenta?{' '}
                <Link to="/login" className="text-cobalt-500 hover:text-cobalt-600 font-semibold">
                  Inicia sesión aquí
                </Link>
              </p>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-2xl">

          {/* Pantalla de confirmación de email */}
          {emailConfirmacion ? (
            <div className="bg-white rounded-lg border-2 border-green-200 p-10 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-50 rounded-full mb-6">
                <i className="ri-mail-check-line text-4xl text-green-600"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                ¡Casi listo!
              </h2>
              <p className="text-gray-600 mb-2 text-base">
                Revisa tu email para activar tu cuenta de {tipoPerfil === 'vendedor' ? 'vendedor' : 'comprador'}.
              </p>
              <p className="text-gray-500 text-sm mb-6">
                Te enviamos un enlace de activación a <strong>{email}</strong>. 
                Haz clic en ese enlace y luego podrás iniciar sesión.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-700 mb-6">
                <i className="ri-spam-line mr-2"></i>
                Si no ves el correo, revisa tu carpeta de Spam o Correo No Deseado.
              </div>
              <a
                href="/login"
                className="inline-block bg-cobalt-500 hover:bg-cobalt-600 text-white font-semibold py-3 px-8 rounded-lg transition-colors whitespace-nowrap"
              >
                Ir a Iniciar Sesión
              </a>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <button
                  onClick={() => setTipoPerfil(null)}
                  className="text-gray-600 hover:text-gray-900 flex items-center text-sm cursor-pointer"
                >
                  <i className="ri-arrow-left-line mr-1"></i>
                  Cambiar tipo de cuenta
                </button>
              </div>

              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-cobalt-50 rounded-lg mb-4">
                  <i className={`${tipoPerfil === 'vendedor' ? 'ri-store-line' : 'ri-shopping-cart-line'} text-3xl text-cobalt-500`}></i>
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Registro de {tipoPerfil === 'vendedor' ? 'Vendedor' : 'Comprador'}
                </h1>
                <p className="text-gray-600">
                  {tipoPerfil === 'vendedor' 
                    ? 'Completa tus datos para comenzar a vender'
                    : 'Crea tu cuenta para comenzar a comprar'}
                </p>
              </div>

              <div className="bg-white rounded-lg border-2 border-gray-200 p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                      <i className="ri-error-warning-line mt-0.5 shrink-0"></i>
                      <span>{error}</span>
                      {error.includes('ya está registrado') && (
                        <a href="/login" className="ml-auto shrink-0 font-semibold underline hover:no-underline">
                          Iniciar sesión
                        </a>
                      )}
                    </div>
                  )}

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Correo Electrónico *
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent outline-none transition-all"
                      placeholder="tu@email.com"
                      required
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                        Contraseña *
                      </label>
                      <input
                        type="password"
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent outline-none transition-all"
                        placeholder="Mínimo 6 caracteres"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                        Confirmar Contraseña *
                      </label>
                      <input
                        type="password"
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent outline-none transition-all"
                        placeholder="Repite tu contraseña"
                        required
                      />
                    </div>
                  </div>

                  {tipoPerfil === 'comprador' && (
                    <div>
                      <label htmlFor="telefonoComprador" className="block text-sm font-medium text-gray-700 mb-2">
                        Teléfono / WhatsApp <span className="text-gray-400 font-normal">(opcional)</span>
                      </label>
                      <input
                        type="tel"
                        id="telefonoComprador"
                        value={telefonoComprador}
                        onChange={(e) => setTelefonoComprador(e.target.value.replace(/\D/g, ''))}
                        className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent outline-none transition-all"
                        placeholder="7871234567"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Los vendedores de Autos y Bienes Raíces podrán contactarte por WhatsApp
                      </p>
                    </div>
                  )}

                  {tipoPerfil === 'vendedor' && (
                    <>
                      <div className="border-t pt-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                          Información del Vendedor
                        </h3>
                      </div>

                      <div>
                        <label htmlFor="nombreCompleto" className="block text-sm font-medium text-gray-700 mb-2">
                          Nombre Completo *
                        </label>
                        <input
                          type="text"
                          id="nombreCompleto"
                          value={nombreCompleto}
                          onChange={(e) => setNombreCompleto(e.target.value)}
                          className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent outline-none transition-all"
                          placeholder="Juan Pérez"
                          required
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="pueblo" className="block text-sm font-medium text-gray-700 mb-2">
                            Pueblo de Puerto Rico *
                          </label>
                          <select
                            id="pueblo"
                            value={pueblo}
                            onChange={(e) => setPueblo(e.target.value)}
                            className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent outline-none transition-all"
                            required
                          >
                            <option value="">Selecciona tu pueblo</option>
                            {pueblosPuertoRico.map((p) => (
                              <option key={p} value={p}>{p}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-2">
                            Teléfono *
                          </label>
                          <input
                            type="tel"
                            id="telefono"
                            value={telefono}
                            onChange={(e) => setTelefono(e.target.value.replace(/\D/g, ''))}
                            className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent outline-none transition-all"
                            placeholder="7871234567"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="direccion" className="block text-sm font-medium text-gray-700 mb-2">
                          Dirección Completa *
                        </label>
                        <input
                          type="text"
                          id="direccion"
                          value={direccion}
                          onChange={(e) => setDireccion(e.target.value)}
                          className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent outline-none transition-all"
                          placeholder="Calle, número, urbanización, código postal"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Esta dirección se usará para coordinar el recogido de artículos con Island Wide
                        </p>
                      </div>

                      <div>
                        <label htmlFor="tipoIdentificacion" className="block text-sm font-medium text-gray-700 mb-2">
                          Tipo de Identificación *
                        </label>
                        <select
                          id="tipoIdentificacion"
                          value={tipoIdentificacion}
                          onChange={(e) => setTipoIdentificacion(e.target.value as TipoIdentificacion)}
                          className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent outline-none transition-all"
                          required
                        >
                          <option value="licencia">Licencia de Conducir</option>
                          <option value="pasaporte">Pasaporte</option>
                          <option value="id_estatal">ID Estatal</option>
                          <option value="real_id">REAL ID</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="numeroIdentificacion" className="block text-sm font-medium text-gray-700 mb-2">
                          Número de Identificación *
                        </label>
                        <input
                          type="text"
                          id="numeroIdentificacion"
                          value={numeroIdentificacion}
                          onChange={(e) => setNumeroIdentificacion(e.target.value)}
                          className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-cobalt-500 focus:border-transparent outline-none transition-all"
                          placeholder="Número de tu identificación"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="archivoIdentificacion" className="block text-sm font-medium text-gray-700 mb-2">
                          Imagen de Identificación *
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                          Sube una foto clara de tu Licencia de Conducir o ID oficial (JPG, PNG o PDF - Máx. 5MB)
                        </p>
                        
                        <div className="relative">
                          <input
                            type="file"
                            id="archivoIdentificacion"
                            accept="image/jpeg,image/jpg,image/png,application/pdf"
                            onChange={handleFileChange}
                            className="hidden"
                            required
                          />
                          <label
                            htmlFor="archivoIdentificacion"
                            className="flex items-center justify-center w-full px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-cobalt-500 transition-colors cursor-pointer bg-gray-50"
                          >
                            {archivoIdentificacion ? (
                              <div className="text-center">
                                <i className="ri-file-check-line text-4xl text-green-600 mb-2"></i>
                                <p className="text-sm font-medium text-gray-900">
                                  {archivoIdentificacion.name}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {(archivoIdentificacion.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                                <p className="text-xs text-cobalt-500 mt-2">
                                  Haz clic para cambiar
                                </p>
                              </div>
                            ) : (
                              <div className="text-center">
                                <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-2"></i>
                                <p className="text-sm font-medium text-gray-700">
                                  Haz clic para subir tu identificación
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  JPG, PNG o PDF (máx. 5MB)
                                </p>
                              </div>
                            )}
                          </label>
                        </div>

                        {previewUrl && (
                          <div className="mt-4">
                            <p className="text-xs font-medium text-gray-700 mb-2">Vista previa:</p>
                            <img
                              src={previewUrl}
                              alt="Preview"
                              className="w-full max-w-md mx-auto rounded-lg border border-gray-300"
                            />
                          </div>
                        )}

                        {uploading && (
                          <div className="mt-4">
                            <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                              <span>Subiendo identificación...</span>
                              <span>{uploadProgress}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-cobalt-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${uploadProgress}%` }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Checkbox T&C */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="aceptaTerminos"
                      checked={aceptaTerminos}
                      onChange={(e) => setAceptaTerminos(e.target.checked)}
                      className="mt-1 w-4 h-4 rounded border-gray-300 accent-gray-900 cursor-pointer shrink-0"
                    />
                    <label htmlFor="aceptaTerminos" className="text-sm text-gray-600 cursor-pointer leading-relaxed">
                      Acepto los{' '}
                      <a
                        href="/terminos"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-900 font-semibold underline hover:no-underline"
                      >
                        Términos y Condiciones
                      </a>{' '}
                      y la{' '}
                      <a
                        href="/privacidad"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-900 font-semibold underline hover:no-underline"
                      >
                        Política de Privacidad
                      </a>{' '}
                      de IslaVende. Entiendo que los artículos de segunda mano, vehículos y bienes raíces se venden <em>as-is</em>.
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || uploading || !aceptaTerminos}
                    className="w-full bg-gray-900 hover:bg-gray-800 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {loading ? 'Creando cuenta...' : uploading ? 'Subiendo identificación...' : 'Crear Cuenta'}
                  </button>
                </form>
              </div>

              <div className="mt-6 text-center">
                <p className="text-gray-600 text-sm">
                  ¿Ya tienes cuenta?{' '}
                  <a href="/login" className="text-cobalt-500 hover:text-cobalt-600 font-semibold">
                    Inicia sesión aquí
                  </a>
                </p>
              </div>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}